import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import WorldMap from "../assets/ethiopiaMap.png";

function getUserInitials(user) {
  if (!user) return "";
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  if (!name) return "";
  const parts = name.split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "";
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

const DashboardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className="h-4 w-4"
  >
    <rect x="4" y="4" width="6" height="6" rx="1" />
    <rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" />
    <rect x="14" y="14" width="6" height="6" rx="1" />
  </svg>
);

export default function PublicHeader({ onOpenLogin }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userInitials = useMemo(() => getUserInitials(user), [user]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [methodsOpen, setMethodsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const menuRef = useRef(null);
  const openLoginModal = onOpenLogin || (() => {});

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    try {
      await logout?.();
    } catch (err) {
      console.warn("Logout failed:", err);
    }
    setMenuOpen(false);
    navigate("/", { replace: true });
  };

  const goToProfile = () => {
    setMenuOpen(false);
    navigate("/perfil");
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-sm shadow-sm">
        <div
            className="
              mx-auto flex h-[64px] w-full max-w-[1600px]
              items-center justify-between
              px-5 sm:px-8 lg:px-10
            "
          >
          <div className="flex items-center gap-10">
            {/* Brand */}
            <Link to="/">
            <div className="flex items-center gap-3">
              
              <img
                src={WorldMap}
                alt="Ethiopia"
                className="h-[38px] w-[48px] object-contain"
              />

              <div className="text-[12px] font-extrabold uppercase tracking-[0.08em] text-slate-900">                
                  Public Policy Lab · Ethiopia
                
              </div>
            </div>
            </Link>

            {/* Navigation */}
            <nav className="hidden items-center gap-12 lg:flex">
              <Link
                to="/"
                className="text-[12px] font-semibold text-slate-800 transition hover:text-emerald-700"
              >
                Home
              </Link>
              {user ? (
                <Link
                  to="/pathways"
                  className="text-[12px] font-semibold text-slate-800 transition hover:text-emerald-700"
                >
                  Explore pathways
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={openLoginModal}
                  className="text-[12px] font-semibold text-slate-800 transition hover:text-emerald-700"
                >
                  Explore pathways
                </button>
              )}

              <button
                type="button"
                onClick={() => setMethodsOpen(true)}
                className="text-[12px] font-semibold text-slate-800 transition hover:text-emerald-700"
              >
                Methods
              </button>

              <button
                type="button"
                onClick={() => setAboutOpen(true)}
                className="text-[12px] font-semibold text-slate-800 transition hover:text-emerald-700"
              >
                About
              </button>
            </nav>
          </div>

          {/* Right */}
          <div className="flex items-center gap-4">
            <Link
              to="/public-results"
              className="
                inline-flex items-center gap-2
                rounded-md
                bg-[#00654f]
                px-5 py-3
                text-[11px] font-semibold text-white
                shadow-sm
                transition
                hover:bg-[#005343]
              "
            >
              <DashboardIcon />
              Open dashboard
            </Link>

            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-full bg-[#00654f] px-4 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#005343]"
                  aria-label="Open user menu"
                >
                  {userInitials || "?"}
                </button>

                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-40 rounded-2xl border border-slate-200 bg-white py-2 shadow-xl">
                    <button
                      type="button"
                      onClick={goToProfile}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                    >
                      Profile
                    </button>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={openLoginModal}
                className="
                  inline-flex items-center justify-center
                  rounded-md
                  border border-[#00654f]
                  bg-white
                  px-5 py-3
                  text-[11px] font-semibold text-[#00654f]
                  transition
                  hover:bg-[#00654f]
                  hover:text-white
                "
              >
                Login
              </button>
            )}
          </div>
        </div>
    </header>

    {methodsOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
        role="dialog"
        aria-modal="true"
        onClick={() => setMethodsOpen(false)}
      >
        <div
          className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.18)] backdrop-blur-md"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[18px] font-bold uppercase tracking-[0.22em] text-[#08725b]">
                METHODS
              </p>
            </div>
            <button
              type="button"
              className="text-slate-400 transition hover:text-slate-700"
              onClick={() => setMethodsOpen(false)}
              aria-label="Close methods modal"
            >
              ×
            </button>
          </div>

          <div className="mt-6 space-y-5 text-lg leading-7 text-slate-700">
            <p>
              The dashboard follows the FABLE analytical approach to model long-term interactions between food systems, land use, greenhouse gas emissions, and biodiversity.
            </p>
            <p>
              Baseline socio-economic, agricultural, land use, and environmental data were compiled from FAOSTAT and national reports of Ethiopia. The baseline period covers 2000 to 2020.
            </p>
            <p>
              Policy pathways and scenario targets were developed using key national policy documents, including Ethiopia’s Ten-Year Development Plan, Nationally Determined Contribution, and Long-Term Low Emission Development Strategy.
            </p>
            <p>
              The dashboard projects changes through 2050 under different combinations of policy pathways and scenarios. These scenarios reflect possible changes in food demand, agricultural production, land use, emissions, and biodiversity outcomes.
            </p>
            <p>
              The results help users compare policy options, examine trade-offs, and assess how different development pathways influence Ethiopia’s food, land, climate, and biodiversity objectives.
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="rounded-2xl bg-[#00654f] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#005343]"
              onClick={() => setMethodsOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {aboutOpen && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
        role="dialog"
        aria-modal="true"
        onClick={() => setAboutOpen(false)}
      >
        <div
          className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-[0_24px_80px_rgba(15,23,42,0.18)] backdrop-blur-md"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[18px] font-bold uppercase tracking-[0.22em] text-[#08725b]">
                ABOUT
              </p>
            </div>
            <button
              type="button"
              className="text-slate-400 transition hover:text-slate-700"
              onClick={() => setAboutOpen(false)}
              aria-label="Close about modal"
            >
              ×
            </button>
          </div>

          <div className="mt-6 space-y-5 text-lg leading-7 text-slate-700">
            <p>
              The Public Policy Lab dashboard for Ethiopia presents projected changes in food systems, land use, greenhouse gas emissions, and biodiversity through 2050.
            </p>
            <p>
              The dashboard uses Ethiopia-specific data and policy targets to help decision-makers explore alternative development pathways. It shows how different scenario combinations affect food production, land allocation, emissions, and biodiversity outcomes over time.
            </p>
            <p>
              The dashboard supports evidence-based policy dialogue by allowing users to compare pathways, assess trade-offs, and understand the long-term implications of policy choices. It helps identify development options aligned with national priorities for food security, sustainable land use, climate action, and biodiversity conservation.
            </p>
            <p>
              The dashboard was developed using the FABLE approach, led by the Alliance of Bioversity International and CIAT in collaboration with the Sustainable Development Solutions Network.
            </p>
            <p>
              <a
                href="https://alliancebioversityciat.org/"
                target="_blank"
                rel="noreferrer"
                className="text-[#08725b] hover:text-[#06513d] font-semibold"
              >
                The Alliance of Bioversity International and CIAT
              </a>{" "}
              is part of{' '}
              <a
                href="https://www.cgiar.org"
                target="_blank"
                rel="noreferrer"
                className="text-[#08725b] hover:text-[#06513d] font-semibold"
              >
                CGIAR
              </a>{' '}
              , a global research partnership for a food-secure future.
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="button"
              className="rounded-2xl bg-[#00654f] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#005343]"
              onClick={() => setAboutOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
